import { Badge } from '@/components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { BookText, Pickaxe, GraduationCap, Users } from 'lucide-react';

interface ChronologyBadgeProps {
    chronology: BiblicalChronology;
    className?: string;
}

const chronologyConfig: Record<BiblicalChronology, {
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bgColor: string;
}> = {
    'Textual': {
        label: 'Textual',
        description: 'Traditional dating based primarily on biblical texts (e.g., early Exodus 1446 BC from 1 Kings 6:1, Mosaic authorship)',
        icon: BookText,
        color: 'text-blue-700 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-950 border-blue-300 dark:border-blue-700'
    },
    'Archaeological': {
        label: 'Archaeological',
        description: 'Dating favoring archaeological evidence where it differs from texts (e.g., late Exodus 1250 BC from destruction layers, Merneptah Stele)',
        icon: Pickaxe,
        color: 'text-amber-700 dark:text-amber-400',
        bgColor: 'bg-amber-100 dark:bg-amber-950 border-amber-300 dark:border-amber-700'
    },
    'Critical': {
        label: 'Critical',
        description: 'Critical scholarship dating (e.g., 6th century BC Torah composition, post-AD 70 Gospel dating, 2nd century BC Daniel)',
        icon: GraduationCap,
        color: 'text-purple-700 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-950 border-purple-300 dark:border-purple-700'
    },
    'Consensus': {
        label: 'Consensus',
        description: 'Broad scholarly consensus across frameworks (e.g., Paul\'s letters 50s-60s AD, Jesus\' ministry 30-33 AD, Fall of Jerusalem 586 BC)',
        icon: Users,
        color: 'text-green-700 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-950 border-green-300 dark:border-green-700'
    }
};

export function ChronologyBadge({ chronology, className = '' }: ChronologyBadgeProps) {
    const config = chronologyConfig[chronology];
    const Icon = config.icon;

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Badge
                        variant="outline"
                        className={`text-xs ${config.color} ${config.bgColor} ${className}`}
                    >
                        <Icon className="h-3 w-3 mr-1" />
                        {config.label}
                    </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <p className="text-sm">{config.description}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

